import type { HTMLChakraProps } from "@chakra-ui/react";
import { Box } from "@chakra-ui/react";
import { type JSX, Suspense } from "react";
import { createLazyLoader } from "utils/createLazyLoader";

import type { Illustrations } from "./illustrationTypes";

export const ILLUSTRATION_SIZE = {
  icon: "20",
  large: "250",
  medium: "200",
  small: "150",
  xsmall: "32",
};
export type IllustrationSize = keyof typeof ILLUSTRATION_SIZE | number;

const getSize = (sizeName: IllustrationSize): string => {
  if (typeof sizeName !== "number" && sizeName in ILLUSTRATION_SIZE) {
    return ILLUSTRATION_SIZE[sizeName as keyof typeof ILLUSTRATION_SIZE] + "px";
  }
  return sizeName + "px";
};

type SvgComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

const getLazyIllustration = createLazyLoader(
  import.meta.glob<{ default: SvgComponent }>(
    "../../assets/illustrations/**/*.svg",
    { query: "?react" },
  ),
);

export interface IllustrationProps extends Omit<
  HTMLChakraProps<"div">,
  "width" | "height"
> {
  name: Illustrations;
  size?: IllustrationSize;
  width?: string;
  height?: string;
  /**
   * Accessible name for illustrations that carry meaning on their own.
   * Without it the illustration is decorative and hidden from assistive
   * technology.
   */
  label?: string;
}

/**
 * Add visual context, warmth, or emphasis to empty states, onboarding,
 * promotional surfaces, and other moments where imagery helps users understand
 * or engage with the interface. For compact functional meaning, use an Icon
 * instead.
 */
export const Illustration = ({
  name,
  size = "medium",
  width,
  height,
  label,
  ...customStyles
}: IllustrationProps): JSX.Element | null => {
  const path = `../../assets/illustrations/${name}.svg`;
  const LazyComponent = getLazyIllustration(path);

  if (!LazyComponent) {
    if (import.meta.env.DEV) {
      console.warn(`[Illustration] Missing illustration: "${name}"`);
    }
    return null;
  }

  const w = width ?? getSize(size);
  const h = height ?? getSize(size);

  return (
    <Box height={h} {...customStyles}>
      {/* The marker lets the Playwright screenshot helper wait for the SVG
          chunk; the empty box is otherwise indistinguishable from content. */}
      <Suspense
        fallback={<Box width={w} height={h} data-illustration-pending />}
      >
        <LazyComponent
          width={w}
          height={h}
          {...(label
            ? { role: "img", "aria-label": label }
            : { "aria-hidden": true })}
        />
      </Suspense>
    </Box>
  );
};

export type { Illustrations } from "./illustrationTypes";
